/**
 * Stable tag tuples and span names for Latitude `capture` on structured `AI.generate` calls.
 * Use with `GenerateInput.telemetry` / `GenerateTelemetryCapture`.
 *
 * Human-facing catalog of each feature (purpose, tags, call sites): `dev-docs/ai-generation-features.md`.
 */
export const AI_GENERATE_TELEMETRY_TAGS = {
  signalDetails: ["issue:details"],
  annotationEnrichPublication: ["annotation:enrichment"],
  flaggerClassify: ["flagger:classify"],
  // The LLM call that summarizes the flagged agent's system prompt into a bounded
  // instruction set before classification. Cached per (org, system prompt) hash.
  flaggerExtractInstructions: ["flagger:extract-instructions"],
  flaggerDraft: ["flagger:draft"],
  // Appended to a flagger's own LLM calls when the trace being flagged is itself
  // flagger-generated. Marks the output so it is never re-flagged, bounding the
  // flagger-on-flagger recursion to a single level. See @domain/flaggers reflag helpers.
  flaggerNoReflag: ["flagger:no-reflag"],
  evaluationJudgeLive: ["eval:execute", "live"],
  evaluationJudgeAlignment: ["eval:execute", "alignment"],
  evaluationJudgeOptimization: ["eval:execute", "optimization"],
  signalGeneration: ["signal:generation"],
  evaluationProposeOptimization: ["gepa:propose"],
  // First taxonomy-naming LLM call: proposes candidate topic themes for a cluster.
  taxonomyProposeThemes: ["taxonomy:propose-themes"],
  // Second taxonomy-naming LLM call: collapses the candidates into one cluster name + description.
  taxonomyNameCluster: ["taxonomy:name-cluster"],
} as const satisfies Record<string, readonly string[]>

export const AI_GENERATE_TELEMETRY_SPAN_NAMES = {
  signalDetails: "issue.details",
  annotationEnrichPublication: "annotation.enrich.publication",
  flaggerClassify: "flagger.classify",
  flaggerExtractInstructions: "flagger.extract-instructions",
  flaggerDraft: "flagger.draft",
  evaluationJudgeLive: "evaluation.judge.live",
  evaluationJudgeAlignment: "evaluation.judge.alignment",
  evaluationJudgeOptimization: "evaluation.judge.optimization",
  signalGeneration: "signal.generation",
  evaluationProposeOptimization: "evaluation.propose.optimization",
  taxonomyProposeThemes: "taxonomy.propose-themes",
  taxonomyNameCluster: "taxonomy.name-cluster",
} as const satisfies Record<string, string>

export type ProjectScopedAiIds = {
  readonly organizationId: string
  readonly projectId: string
}

/**
 * Metadata for AI generate telemetry: always includes org + project, then subject fields with `undefined` omitted.
 */
export const buildProjectScopedAiMetadata = (
  scope: ProjectScopedAiIds,
  subject: Record<string, string | number | boolean | null | undefined>,
): Record<string, unknown> => {
  const metadata: Record<string, unknown> = {
    organizationId: scope.organizationId,
    projectId: scope.projectId,
  }
  for (const [key, value] of Object.entries(subject)) {
    if (value !== undefined) {
      metadata[key] = value
    }
  }
  return metadata
}
